import { Component } from "react"
import PropTypes from "prop-types"
import { get } from "shared/lib/api"
import range from "lodash/range"
import Spinner from "shared/components/spinner"
import TransactionLogTable from "./transaction_log/main"
import * as FilterOptions from "./transaction_log/filter_options"

import "./dynamic_applet.scss"

class Logs extends Component {
  constructor(props) {
    super(props)

    this.state = {
      requestType: FilterOptions.requestTypes[0].id,
      responseStatus: FilterOptions.responseStatuses[0].id,
      timeframe: "1day",
      query: "",
      data: [],
      page: 1,
      pages: 0,
      submitting: false,
    }
  }

  componentDidMount() {
    this.queryTransactionLogs()
  }

  queryTransactionLogs = () => {
    this.setState({ submitting: true })

    const url =
      this.props.transactions_url +
      `?page=${this.state.page}&request_type=${this.state.requestType}&response_status=${this.state.responseStatus}&timeframe=${this.state.timeframe}&query=${this.state.query}`

    // page.count is 0 for one page results, 1 for two page results, etc...
    // page.number is 0 based
    get(url)
      .then(resp => {
        this.setState({
          data: resp.data,
          page: resp.page.number + 1,
          pages: resp.page.count + 1,
        })
      })
      .catch(() =>
        window.appendFlash(
          "Couldn't load log data. Pleas try again later.",
          "danger"
        )
      )
      .finally(() => this.setState({ submitting: false }))
  }

  updateFilter = filterName => {
    return event =>
      this.setState(
        { [filterName]: event.target.value },
        this.queryTransactionLogs
      )
  }

  handleSearchKeyDown = event => {
    if (event.key === "Enter") {
      this.setState({ query: event.target.value }, this.queryTransactionLogs)
    }
  }

  handlePrevPageClick = event => {
    event.preventDefault()
    this.goToPage(this.state.page - 1)
  }

  handleNextPageClick = event => {
    event.preventDefault()
    this.goToPage(this.state.page + 1)
  }

  handlePageClick = page => event => {
    event.preventDefault()
    this.goToPage(page)
  }

  goToPage = page => {
    const pages = this.state.pages || 0
    if (page >= 1 && page <= pages) {
      this.setState({ page }, this.queryTransactionLogs)
    }
  }

  createIsland = (center, radius) => {
    return range(center - radius, center + radius + 1)
  }

  getPageNumbers = () => {
    let pageNumbers = []
    if (this.state.pages < 10) {
      // just show all page numbers
      pageNumbers = range(1, this.state.pages + 1)
    } else {
      // the idea here is that the entire range of page numbers is represented as three islands: 1...x...n
      // the first island (1...) is for the numbers around the first page
      // the center island (...x...) is for the numbers around the current page
      // the last island (...n) is for the numbers around the last page

      // how many page numbers to show after the first page
      const firstIslandRadius = 2
      // how many page numbers to show before the last page
      const lastIslandRadius = firstIslandRadius
      // how many page numbers to show around the current page
      const centerIslandRadius = 2
      // number that indicates a gap in page numbers, space between islands
      const ellipsis = 0

      // create islands
      const firstIsland = this.createIsland(1, firstIslandRadius)
      const centerIsland = this.createIsland(
        this.state.page,
        centerIslandRadius
      )
      const lastIsland = this.createIsland(this.state.pages, lastIslandRadius)

      // dedup
      const unique = new Set([...firstIsland, ...centerIsland, ...lastIsland])

      pageNumbers = Array.from(unique)
      // sort
      pageNumbers.sort((a, b) => a - b)
      // remove out-of-bound numbers
      pageNumbers = pageNumbers.filter(number => {
        return number >= 1 && number <= this.state.pages
      })
      // insert ellipsis between the first and the center island
      if (firstIslandRadius + 2 < this.state.page - centerIslandRadius) {
        pageNumbers.splice(firstIslandRadius + 1, 0, ellipsis)
      }
      // insert ellipsis between the center and the last island
      if (
        this.state.page + centerIslandRadius + 1 <
        this.state.pages - lastIslandRadius
      ) {
        pageNumbers.splice(
          pageNumbers.length - lastIslandRadius - 1,
          0,
          ellipsis
        )
      }
    }
    return pageNumbers
  }

  render() {
    const spinnerConfig = {
      position: "relative",
      top: "30px",
      length: 6,
      width: 4,
      radius: 20,
      corners: 0,
    }

    const pageNumbers = this.getPageNumbers()

    return (
      <section styleName="dynamic_applet logs">
        <div styleName="fieldset">
          <div>
            <select
              onChange={this.updateFilter("requestType")}
              value={this.state.requestType}
            >
              {FilterOptions.requestTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div styleName="middle-element">
            <select
              onChange={this.updateFilter("responseStatus")}
              value={this.state.responseStatus}
            >
              {FilterOptions.responseStatuses.map(type => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <select
              onChange={this.updateFilter("timeframe")}
              value={this.state.timeframe}
            >
              {FilterOptions.timeframes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div styleName="fieldset">
          <div styleName="search-box">
            <input
              type="text"
              placeholder="Search by request id or user id"
              onKeyDown={this.handleSearchKeyDown}
            />
          </div>
        </div>

        {this.state.submitting ? (
          <div>
            <Spinner config={spinnerConfig} />
          </div>
        ) : (
          <>
            {this.state.data.length == 0 ? (
              <div>No requests available</div>
            ) : (
              <TransactionLogTable {...this.state} />
            )}
          </>
        )}
        {this.state.data.length > 0 && (
          <section styleName="pagination" className="pagination">
            <section
              className="pagination-prev-page"
              onClick={this.handlePrevPageClick}
            >
              {this.state.page > 1 ? <a href="#">Previous</a> : <>Previous</>}
            </section>
            <ul className="pagination-page-numbers">
              {pageNumbers.map((page, i) => (
                <li key={i} onClick={this.handlePageClick(page)}>
                  {page === this.state.page || page === 0 ? (
                    <>{page === 0 ? <>&hellip;</> : page}</>
                  ) : (
                    <a href="#">{page}</a>
                  )}
                </li>
              ))}
            </ul>
            <section
              className="pagination-prev-page"
              onClick={this.handleNextPageClick}
            >
              {this.state.page < this.state.pages ? (
                <a href="#">Next</a>
              ) : (
                <>Next</>
              )}
            </section>
          </section>
        )}
      </section>
    )
  }
}

Logs.propTypes = {
  transactions_url: PropTypes.string.isRequired,
}

export default Logs
